import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Code,
  Paintbrush,
  Users,
  Zap,
  MousePointerClick,
  Terminal,
} from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/supportfyi-logo.svg";
import { DemoPreview } from "@/components/DemoPreview";
import { useAuth } from "@/hooks/useAuth";

const LandingPage = () => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 h-14">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground"
          >
            <img src={logo} alt="supportfyi" className="h-5 w-5" />
            supportfyi
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button size="sm">Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm">Sign up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">
          {/* Text + CTA */}
          <div className="flex-1 text-center lg:text-left space-y-6 lg:max-w-sm">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-gradient">
              Status banners for your website, in one line of code
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Create, style, and push live banners to any site. No deploys, no
              code changes — just paste a script tag to set it up.
            </p>
            <div>
              <Link to="/signup">
                <Button size="lg" className="text-base px-8 h-12 font-semibold">
                  Get started free
                </Button>
              </Link>
            </div>
          </div>

          {/* Preview — side by side on lg, below CTA on mobile */}
          <div className="w-full lg:flex-1">
            <DemoPreview />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 border-t border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-16 tracking-tight">
            How it works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Add the script",
                desc: "Paste one line into your HTML. That's the only setup you'll ever do.",
                icon: Terminal,
              },
              {
                step: "02",
                title: "Create a banner",
                desc: "Write your message, pick colors, and preview it live in the dashboard.",
                icon: Paintbrush,
              },
              {
                step: "03",
                title: "Go live instantly",
                desc: "Activate the banner and it appears on your site. No deploy needed.",
                icon: Zap,
              },
            ].map((item) => (
              <div key={item.step} className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-muted-foreground">
                    {item.step}
                  </span>
                  <item.icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 border-t border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-16 tracking-tight">
            Everything you need
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "Live banner editor",
                desc: "WYSIWYG editor with real-time preview. See exactly what your visitors see.",
                icon: Paintbrush,
              },
              {
                title: "Custom branding",
                desc: "Match your brand with custom colors, fonts, border radius, and positioning.",
                icon: Code,
              },
              {
                title: "Team collaboration",
                desc: "Invite your team. Manage roles and permissions across your organization.",
                icon: Users,
              },
              {
                title: "One-line embed",
                desc: "A single script tag. Works with any framework, CMS, or static site.",
                icon: Terminal,
              },
              {
                title: "Instant toggle",
                desc: "Activate or deactivate banners with one click. No code changes required.",
                icon: MousePointerClick,
              },
              {
                title: "Zero performance hit",
                desc: "Lightweight script loads asynchronously. Your site stays fast.",
                icon: Zap,
              },
            ].map((feature) => (
              <Card
                key={feature.title}
                className="bg-card border-border/60 hover:border-border transition-colors"
              >
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-2.5">
                    <feature.icon className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">{feature.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 px-6 border-t border-border/50">
        <div className="max-w-xl mx-auto text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Ready to ship your first banner?
          </h2>
          <p className="text-muted-foreground !mb-4">
            Free to start. No credit card required.
          </p>
          <Link to="/signup">
            <Button size="lg" className="text-base px-8 h-12 font-semibold">
              Start for free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} supportfyi</span>
          <div className="flex gap-4">
            <Link
              to="/login"
              className="hover:text-foreground transition-colors"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="hover:text-foreground transition-colors"
            >
              Sign up
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
